import { For, Show } from "solid-js";
import {
  columnHeaderForFeature,
  formatImportanceSharePercent,
  importanceDisplayCellClasses,
  type PerformanceDriversTableRow,
} from "../../utils/performanceDriversModelFit";
import {
  performanceDriversImportanceCellTooltip,
  performanceDriversImportanceExplanation,
  performanceDriversImportanceLegendHigher,
  performanceDriversImportanceLegendLower,
} from "../../utils/performanceDriversExplanation";

export interface PerformanceDriversImportanceTableProps {
  columns: string[];
  rows: PerformanceDriversTableRow[];
  displayNames?: Record<string, string>;
  targetUnit: string;
}

export default function PerformanceDriversImportanceTable(props: PerformanceDriversImportanceTableProps) {
  return (
    <section class="performance-drivers-table-section">
      <h3 class="performance-drivers-section-title">Importance</h3>
      <p class="performance-drivers-section-explanation">
        {performanceDriversImportanceExplanation(props.targetUnit)}
      </p>
      <Show
        when={props.columns.length > 0 && props.rows.length > 0}
        fallback={<p class="performance-drivers-empty">No importance data for this selection.</p>}
      >
        <div class="performance-drivers-importance-legend" aria-hidden="true">
          <span class="performance-drivers-importance-legend-item performance-drivers-importance-slower">
            {performanceDriversImportanceLegendLower(props.targetUnit)}
          </span>
          <span class="performance-drivers-importance-legend-item performance-drivers-importance-neutral">
            Weak / mixed
          </span>
          <span class="performance-drivers-importance-legend-item performance-drivers-importance-faster">
            {performanceDriversImportanceLegendHigher(props.targetUnit)}
          </span>
        </div>
        <p class="performance-drivers-table-caption">
          Cell value = % of setup-driver impact in that TWS row (hover for kph).
        </p>
        <div class="data-table-container">
          <div class="scrollable-container">
            <table class="maneuvers-table performance-drivers-table performance-drivers-importance-table">
              <thead>
                <tr>
                  <th class="performance-drivers-col-tws centered">TWS</th>
                  <For each={props.columns}>
                    {(col) => <th>{columnHeaderForFeature(col, props.displayNames)}</th>}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={props.rows}>
                  {(row) => (
                    <tr class={row.skipped ? "performance-drivers-row-skipped" : undefined}>
                      <td class="performance-drivers-col-tws centered">{row.twsBin}</td>
                      <For each={props.columns}>
                        {(col) => {
                          const share = row.importanceSharePct[col];
                          const kph = row.importanceValues[col];
                          const direction = row.importanceDirection[col];
                          const heatClass = row.skipped
                            ? "performance-drivers-cell-empty"
                            : importanceDisplayCellClasses(share, direction, true);
                          const tooltip =
                            kph != null && Number.isFinite(kph)
                              ? performanceDriversImportanceCellTooltip(
                                  kph,
                                  props.targetUnit,
                                  share,
                                  direction
                                )
                              : undefined;
                          return (
                            <td class={heatClass} title={tooltip}>
                              {formatImportanceSharePercent(share)}
                            </td>
                          );
                        }}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </div>
      </Show>
    </section>
  );
}
